import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { isDismissible, noticeKind } from './noticeKinds';

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

// The unfolded part of a notice: summary (phones only when `summaryOnPhones`),
// detail bullets and link. Shared with the Notices page.
export const NoticeBody = ({ notice, id, summaryOnPhones = true }) => {
  const details = (notice.details || '').split('\n').map((line) => line.trim()).filter(Boolean);
  return (
    <div id={id} className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
      {summaryOnPhones && <p className="md:hidden">{notice.summary}</p>}
      {details.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4">
          {details.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
      {notice.link_url && (
        <a
          href={notice.link_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {notice.link_text || 'Learn more'} →
        </a>
      )}
    </div>
  );
};

// One notice: severity dot, title and (md+) its one-line summary; unfolds to
// its body.
const NoticeItem = ({ notice, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const kind = noticeKind(notice.kind);
  const bodyId = `notice-${notice.id}`;

  let action;
  if (isDismissible(notice)) {
    action = (
      <button
        type="button"
        onClick={() => onDismiss?.(notice)}
        aria-label={`Dismiss: ${notice.title}`}
        className={`flex-none rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 ${focusRing}`}
      >
        <XMarkIcon aria-hidden="true" className="h-4 w-4" />
      </button>
    );
  } else {
    action = (
      <span title="Stays until the event" className="flex-none font-mono text-[10px] uppercase tracking-wider text-gray-400">
        pinned
      </span>
    );
  }

  return (
    <li className="py-2">
      <div className="flex items-center gap-2 text-sm">
        <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${kind.dot}`} />
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((value) => !value)}
          className={`flex min-w-0 flex-1 items-center gap-2 rounded text-left ${focusRing}`}
        >
          <span className="sr-only">{kind.label}: </span>
          <span className="truncate font-medium text-gray-900 dark:text-gray-100 md:flex-none md:max-w-[50%]">
            {notice.title}
          </span>
          {notice.auto && (
            <span
              title="Generated from the resource's timezone"
              className="flex-none rounded bg-gray-100 px-1.5 py-px font-mono text-[10px] uppercase tracking-wider text-gray-500 dark:bg-gray-800 dark:text-gray-400"
            >
              auto
            </span>
          )}
          <span className="hidden min-w-0 truncate text-gray-500 dark:text-gray-400 md:inline">
            {notice.summary}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className={`ml-auto h-4 w-4 flex-none text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {action}
      </div>
      {open && (
        <div className="mt-1.5 mb-1 ml-4">
          <NoticeBody notice={notice} id={bodyId} />
        </div>
      )}
    </li>
  );
};

/**
 * The dashboard's "Notices · n" panel: a neutral card listing notices
 * (already sorted, dismissed ones left out) with a severity dot each; folds
 * to its header line. Renders nothing when there are none; dismissed notices
 * are found again on the Notices page.
 *
 * @param {{notices: object[], onDismiss?: Function, allNoticesLink?: boolean,
 *   className?: string}} props
 */
export default function NoticesPanel({ notices, onDismiss, allNoticesLink = false, className = '' }) {
  const [open, setOpen] = useState(true);
  if (!notices?.length) return null;

  return (
    <section
      aria-label="Notices"
      className={`rounded-xl border border-gray-200/70 bg-surface px-4 py-2 dark:border-gray-600/70 dark:bg-gray-870 ${className}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={`flex min-w-0 flex-1 items-center gap-2 rounded py-0.5 text-left ${focusRing}`}
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            Notices · {notices.length}
          </span>
          <span aria-hidden="true" className="flex gap-1">
            {notices.map((notice) => (
              <span key={notice.id} className={`h-1.5 w-1.5 rounded-full ${noticeKind(notice.kind).dot}`} />
            ))}
          </span>
        </button>
        {allNoticesLink && (
          <Link
            to="/notices"
            className={`flex-none rounded text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 ${focusRing}`}
          >
            All notices →
          </Link>
        )}
        <button
          type="button"
          aria-label={open ? 'Collapse notices' : 'Expand notices'}
          onClick={() => setOpen((value) => !value)}
          className={`flex-none rounded ${focusRing}`}
        >
          <ChevronDownIcon
            aria-hidden="true"
            className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {open && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {notices.map((notice) => (
            <NoticeItem key={notice.id} notice={notice} onDismiss={onDismiss} />
          ))}
        </ul>
      )}
    </section>
  );
}
